/**
 * @file AcknowledmentModalStyleA.js
 * @description A specific style of action modal that includes title, subtitle, text, and action buttons.
 */

import style from "./modals.module.scss";
import React, { useEffect, useRef } from 'react';
import PropTypes from "prop-types";
import { getText } from '../../../i18n';

/**
 * AcknowledgementModalStyleA component, rendering a styled modal for confirmations.
 * @param {ModalConfig} modalProps - The properties for configuring the modal.
 * @returns {React.ReactElement} The rendered component.
 */
const AcknowledgementModalStyleA = ({
  title,
  subtitle,
  text,
  onYes,
  onHide,
  yesButtonText = "Ok",
}) => {
  const footerRef = useRef(null);

  useEffect(() => {
    if (footerRef.current) {
      const buttons = footerRef.current.querySelectorAll('.btn');
      let maxWidth = 0;

      buttons.forEach(button => {
        maxWidth = Math.max(maxWidth, button.offsetWidth);
      });

      buttons.forEach(button => {
        button.style.width = `${maxWidth}px`;
      });
    }
  }, []);

  const handleYes = () => {
    onYes();
    onHide(); // Dismiss the modal after the 'Yes' action
  };
  return (
    <div className={`${style.container} modal-backdrop`}>
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{ display: "block" }}
        aria-modal="true"
        role="dialog"
      >
        <div
          className={`modal-dialog modal-lg modal-dialog-centered ${style.modalSizing}`}
        >
          <div className={`modal-content ${style.modalContent}`}>
            <button
              type="button"
              className={`btn-close ${style.dismissBtn}`}
              onClick={onHide}
              aria-label={getText('msg_bbfa773e5a63')}
            />
            <div className={`modal-body text-center ${style.modalBody}`}>
              <div className={`col-12 ${style.SVGIconContainer}`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                >
                  <path
                    d="M51.9508 13.6742C52.6831 14.4064 52.6831 15.5936 51.9508 16.3258L25.7008 42.5758C25.3492 42.9275 24.8723 43.125 24.375 43.125C23.8777 43.125 23.4008 42.9275 23.0492 42.5758L9.92417 29.4508C9.19194 28.7186 9.19194 27.5314 9.92417 26.7992C10.6564 26.0669 11.8436 26.0669 12.5758 26.7992L24.375 38.5984L49.2992 13.6742C50.0314 12.9419 51.2186 12.9419 51.9508 13.6742Z"
                    fill="#53B483"
                  />
                </svg>
              </div>
              {title && (
                <h5 className={`modal-title ${style.title}`}>{title}</h5>
              )}
              {subtitle && <h6 className={`${style.subtitle}`}>{subtitle}</h6>}
              {text && <p className={`${style.textContainer}`}>{text}</p>}

              <div className={`modal-footer ${style.modalFooter}`} ref={footerRef}>
                <button
                  type="button"
                  className="btn ing-success-btn"
                  onClick={handleYes}
                >
                  {yesButtonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AcknowledgementModalStyleA.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  text: PropTypes.string,
  onYes: PropTypes.func.isRequired,
  onNo: PropTypes.func,
  onHide: PropTypes.func.isRequired,
};

export default AcknowledgementModalStyleA;
